import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserType } from '@/types/user/user.type';
import { usePermissionStore } from './permissionStore';
import { setCsrfToken } from '@/utilities/auth/csrfToken';

interface AuthState {
  user: UserType | null;
  tempToken: string | null;
  loginEmail: string | null;
  isAuthenticated: boolean;
  // `persist` reads localStorage asynchronously relative to the first
  // render, so `isAuthenticated` starts as `false` on every hard reload
  // until this flips to `true`. Consumers that redirect based on
  // `isAuthenticated` (e.g. AuthGuard) must wait for this first, or they'll
  // bounce an actually-authenticated user to /login on the stale default.
  hasHydrated: boolean;
  setUser: (user: AuthState['user']) => void;
  setTempToken: (token: string | null) => void;
  setLoginEmail: (email: string | null) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tempToken: null,
      loginEmail: null,
      isAuthenticated: false,
      hasHydrated: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setTempToken: (tempToken) => set({ tempToken }),
      setLoginEmail: (loginEmail) => set({ loginEmail }),
      setHasHydrated: (hasHydrated) => set({ hasHydrated }),

      logout: () => {
        // clear auth
        set({
          user: null,
          tempToken: null,
          loginEmail: null,
          isAuthenticated: false,
        });

        setCsrfToken(null);

        // also clear permissions in the separate permission store
        try {
          usePermissionStore.getState().clearPermissions();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to clear permissions on logout', e);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (_state, error) => {
        // Logging only — `hasHydrated` itself is set right after `create()`
        // below, not here. This callback can fire synchronously inside
        // `create()` (localStorage hydration isn't async), before the
        // `useAuthStore` binding it would otherwise need exists yet.
        if (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to rehydrate auth store:', error);
        }
      },
    },
  ),
);

// `persist` hydrates from localStorage synchronously as part of `create()`
// above, so by this point the real persisted value is already in state —
// this just flips the flag that lets consumers (e.g. AuthGuard) know it's
// safe to trust `isAuthenticated` instead of the initial `false` default.
if (typeof window !== 'undefined') {
  useAuthStore.setState({ hasHydrated: true });
}
